import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Users,
  BookOpen,
  ChevronRight,
  BarChart3,
  Clock,
  CheckCircle,
  FileText,
} from "lucide-react";
import { useInstructorAnalytics } from "@/lib/analytics.hooks";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  ChartTooltip,
  ChartConfig,
} from "@/components/ui/chart";

export const Route = createFileRoute("/_authenticated/instructor/")({
  component: InstructorHome,
});

interface Group {
  id: string;
  name: string;
  schedule_day: string | null;
  schedule_time: string | null;
  is_active: boolean;
  courses: { name: string; symbol: string | null } | null;
  members: { count: number }[];
}

const groupPerformanceConfig = {
  avg_grade: { label: "متوسط الدرجات", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

function InstructorHome() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const { data: analytics, isLoading: analyticsLoading } = useInstructorAnalytics();

  useEffect(() => {
    if (!user) return;
    supabase
      .from("groups")
      .select(
        "id,name,schedule_day,schedule_time,is_active,courses(name,symbol),members:group_members(count)",
      )
      .eq("instructor_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setGroups((data ?? []) as unknown as Group[]);
        setLoading(false);
      });
  }, [user]);

  if (loading || analyticsLoading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold mb-2">لوحة المدرس 🧑‍🏫</h1>
        <p className="text-muted-foreground">نظرة عامة على مجموعاتك وأدائك</p>
      </div>

      {analytics && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-3xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">واجبات بانتظار التقييم</div>
                  <div className="text-3xl font-black">{analytics.pendingReviews}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-primary flex items-center justify-center text-primary-foreground">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">الطلاب النشطين</div>
                  <div className="text-3xl font-black">{analytics.activeStudents}</div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">نسبة الحضور</div>
                  <div className="text-3xl font-black">{analytics.attendanceRate}%</div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">نسبة تسليم الواجبات</div>
                  <div className="text-3xl font-black">{analytics.submissionRate}%</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-3xl border-0 shadow-soft">
            <CardHeader>
              <CardTitle>أداء المجموعات</CardTitle>
              <CardDescription>متوسط درجات الطلاب في الواجبات لكل مجموعة</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.groupPerformance.length === 0 ? (
                <div className="h-[250px] w-full flex flex-col items-center justify-center text-muted-foreground bg-muted/20 rounded-xl border border-dashed">
                  <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
                  <p>لا توجد بيانات متاحة بعد</p>
                </div>
              ) : (
                <ChartContainer config={groupPerformanceConfig} className="h-[250px] w-full">
                  <BarChart data={analytics.groupPerformance} margin={{ top: 20 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="group_name" tickLine={false} axisLine={false} tickMargin={8} />
                    <YAxis tickLine={false} axisLine={false} domain={[0, 100]} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="avg_grade" fill="var(--color-avg_grade)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <div>
        <h2 className="text-2xl font-extrabold mb-4">مجموعاتي</h2>
        {groups.length === 0 ? (
          <Card className="rounded-3xl border-dashed">
            <CardContent className="p-10 text-center text-muted-foreground">
              لم يتم تعيينك على أي جروب بعد. كلم الإدارة.
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((g) => (
              <Link
                key={g.id}
                to="/instructor/group/$groupId"
                params={{ groupId: g.id }}
                className="block"
              >
                <Card className="rounded-3xl border-0 shadow-soft hover:shadow-lg transition cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-3xl">{g.courses?.symbol || "📚"}</div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="font-bold text-lg mb-1">{g.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> {g.courses?.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {g.schedule_day && <Badge variant="secondary">{g.schedule_day}</Badge>}
                      {g.schedule_time && <Badge variant="secondary">{g.schedule_time}</Badge>}
                      <Badge variant="secondary">
                        <Users className="h-3 w-3 me-1" />
                        {g.members?.[0]?.count ?? 0}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
